import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { emailTemplateService } from '../../../services/emailTemplateService';

const TemplateEditor = ({ template, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    templateName: '',
    subjectLine: '',
    templateBody: '',
    segment: 'all_clients',
    status: 'draft'
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');

  useEffect(() => {
    if (template) {
      setFormData({
        templateName: template?.templateName || '',
        subjectLine: template?.subjectLine || '',
        templateBody: template?.templateBody || '',
        segment: template?.segment || 'all_clients',
        status: template?.status || 'draft'
      });
    }
  }, [template]);

  const variables = [
    { name: '{{client_name}}', description: 'Client name' },
    { name: '{{service_count}}', description: 'Total services' },
    { name: '{{total_revenue}}', description: 'Total revenue' },
    { name: '{{avg_invoice}}', description: 'Average invoice value' },
    { name: '{{client_lifetime_value}}', description: 'Client lifetime value' },
    { name: '{{first_service_date}}', description: 'First service date' },
    { name: '{{last_service_date}}', description: 'Last service date' },
    { name: '{{month}}', description: 'Current month' },
    { name: '{{year}}', description: 'Current year' }
  ];

  const handleInsertVariable = (variable) => {
    const textarea = document.getElementById('template-body');
    const start = textarea?.selectionStart;
    const end = textarea?.selectionEnd;
    const text = formData?.templateBody;
    const before = text?.substring(0, start);
    const after = text?.substring(end);
    
    setFormData({
      ...formData,
      templateBody: before + variable + after
    });

    setTimeout(() => {
      textarea?.focus();
      textarea?.setSelectionRange(start + variable?.length, start + variable?.length);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    try {
      setLoading(true);
      
      if (template) {
        await emailTemplateService?.updateTemplate(template?.id, formData);
      } else {
        await emailTemplateService?.createTemplate(formData);
      }
      
      onSave();
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const previewHtml = emailTemplateService?.replaceVariables(
    formData?.templateBody,
    {
      name: 'Juan Pérez',
      serviceCount: 5,
      totalRevenue: 15000,
      avgInvoice: 3000,
      clientLifetimeValue: 15000,
      firstServiceDate: '2024-01-15',
      lastServiceDate: '2026-02-10'
    }
  );

  return (
    <div className="bg-card border border-border rounded-lg">
      <div className="border-b border-border p-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-heading font-bold text-foreground">
            {template ? 'Edit Template' : 'Create New Template'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-muted rounded-lg transition-smooth press-effect focus-ring"
          >
            <Icon name="X" size={24} />
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Template Name
              </label>
              <input
                type="text"
                value={formData?.templateName}
                onChange={(e) => setFormData({ ...formData, templateName: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                placeholder="e.g., High-Value Client Monthly Report"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Client Segment
                </label>
                <select
                  value={formData?.segment}
                  onChange={(e) => setFormData({ ...formData, segment: e?.target?.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  required
                >
                  <option value="all_clients">All Clients</option>
                  <option value="high_value">High-Value Clients</option>
                  <option value="repeat">Repeat Clients</option>
                  <option value="at_risk">At-Risk Clients</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <select
                  value={formData?.status}
                  onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                  required
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Subject Line
              </label>
              <input
                type="text"
                value={formData?.subjectLine}
                onChange={(e) => setFormData({ ...formData, subjectLine: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground"
                placeholder="Your Exclusive Monthly Performance Report - {{month}} {{year}}"
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-foreground">
                  Email Body
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveTab('edit')}
                    className={`px-3 py-1 text-sm rounded-lg transition-smooth ${
                      activeTab === 'edit' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-muted/80'
                    }`}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('preview')}
                    className={`px-3 py-1 text-sm rounded-lg transition-smooth ${
                      activeTab === 'preview' ?'bg-primary text-primary-foreground' :'bg-muted text-foreground hover:bg-muted/80'
                    }`}
                  >
                    Preview
                  </button>
                </div>
              </div>

              {activeTab === 'edit' ? (
                <textarea
                  id="template-body"
                  value={formData?.templateBody}
                  onChange={(e) => setFormData({ ...formData, templateBody: e?.target?.value })}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-background text-foreground font-mono text-sm"
                  rows={16}
                  placeholder="<h1>Dear {{client_name}},</h1><p>Your email content here...</p>"
                  required
                />
              ) : (
                <div
                  className="w-full px-4 py-3 border border-border rounded-lg bg-white text-black min-h-[400px]"
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              )}
            </div>
          </div>

          <div>
            <div className="bg-muted/50 border border-border rounded-lg p-4 sticky top-4">
              <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Code" size={18} />
                Available Variables
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Click to insert into template
              </p>
              <div className="space-y-2">
                {variables?.map((variable) => (
                  <button
                    key={variable?.name}
                    type="button"
                    onClick={() => handleInsertVariable(variable?.name)}
                    className="w-full text-left px-3 py-2 bg-background border border-border rounded-lg hover:bg-primary/10 hover:border-primary transition-smooth press-effect focus-ring"
                  >
                    <div className="font-mono text-sm text-primary">{variable?.name}</div>
                    <div className="text-xs text-muted-foreground">{variable?.description}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-3 pt-6 mt-6 border-t border-border">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-smooth press-effect focus-ring"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth press-effect focus-ring disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Icon name="Loader2" size={20} className="inline mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              template ? 'Update Template' : 'Create Template'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TemplateEditor;